import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Chip,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  SwapHoriz as SwapHorizIcon,
} from "@mui/icons-material";
import { columnMappingAPI } from "../services/api.js";
import { useSnackbar } from "notistack";

const ColumnMappingDialog = ({
  open,
  onClose,
  onConfirm,
  selectedFile,
  databaseName,
  tableName,
}) => {
  const [mappingData, setMappingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customMapping, setCustomMapping] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open && selectedFile && databaseName && tableName) {
      analyzeMappings();
    }
  }, [open, selectedFile, databaseName, tableName]);

  const analyzeMappings = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("database_name", databaseName);
      formData.append("table_name", tableName);

      const response = await columnMappingAPI.previewMapping(formData);
      setMappingData(response.data);

      // Initialize custom mapping with automatic mappings
      const initialMapping = response.data.mapping_analysis.column_mapping;
      setCustomMapping(initialMapping);
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to analyze column mappings",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (excelColumn, targetColumn) => {
    setCustomMapping((prev) => ({
      ...prev,
      [excelColumn]: targetColumn,
    }));
    setValidationErrors([]);
  };

  const validateMappings = async () => {
    try {
      const response = await columnMappingAPI.validateMapping({
        database_name: databaseName,
        table_name: tableName,
        column_mapping: customMapping,
      });

      if (!response.data.valid) {
        setValidationErrors(response.data.errors);
        return false;
      }

      setValidationErrors([]);
      return true;
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to validate mappings",
        { variant: "error" }
      );
      return false;
    }
  };

  const handleConfirm = async () => {
    const isValid = await validateMappings();
    if (isValid) {
      onConfirm(customMapping, mappingData);
    }
  };

  const getMatchTypeIcon = (matchType) => {
    switch (matchType) {
      case "exact":
        return <CheckCircleIcon color="success" fontSize="small" />;
      case "fuzzy":
        return <SwapHorizIcon color="warning" fontSize="small" />;
      case "new":
        return <AddIcon color="info" fontSize="small" />;
      default:
        return null;
    }
  };

  const getMatchTypeColor = (matchType) => {
    switch (matchType) {
      case "exact":
        return "success";
      case "fuzzy":
        return "warning";
      case "new":
        return "info";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Analyzing column mappings...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!mappingData) {
    return null;
  }

  const { excel_info, table_info, mapping_analysis } = mappingData;
  const availableColumns = Object.keys(table_info.existing_columns);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SwapHorizIcon />
          Column Mapping Preview
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Summary Cards */}
        <Box display="flex" gap={2} mb={3}>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              Excel File
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {excel_info.filename}
            </Typography>
            <Typography variant="body2">
              {mapping_analysis.total_excel_columns} columns
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              Target Table
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {table_info.database_name}.{table_info.table_name}
            </Typography>
            <Typography variant="body2">
              {mapping_analysis.total_existing_columns} existing columns
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              New Columns
            </Typography>
            <Typography variant="body2" color="info.main">
              {mapping_analysis.total_new_columns} columns will be created
            </Typography>
          </Paper>
        </Box>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Validation Errors:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Column Mapping Table */}
        <Typography variant="h6" gutterBottom>
          Column Mappings
        </Typography>

        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Excel Column</TableCell>
                <TableCell>Excel Type</TableCell>
                <TableCell>Match Type</TableCell>
                <TableCell>Target Column</TableCell>
                <TableCell>Table Type</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {excel_info.clean_headers.map((excelCol, index) => {
                const excelType = excel_info.column_types[excelCol];
                const targetCol = customMapping[excelCol] || excelCol;
                const tableType = table_info.existing_columns[targetCol];

                // Determine match type
                let matchType = "new";
                let similarity = 0;

                const exactMatch = mapping_analysis.exact_matches.find(
                  (m) => m.excel_column === excelCol
                );
                const fuzzyMatch = mapping_analysis.fuzzy_matches.find(
                  (m) => m.excel_column === excelCol
                );

                if (exactMatch) {
                  matchType = "exact";
                } else if (fuzzyMatch) {
                  matchType = "fuzzy";
                  similarity = fuzzyMatch.similarity;
                }

                return (
                  <TableRow key={excelCol}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="medium">
                          {excel_info.headers[index]}
                        </Typography>
                        {excel_info.headers[index] !== excelCol && (
                          <Typography variant="caption" color="text.secondary">
                            ({excelCol})
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip label={excelType} size="small" variant="outlined" />
                    </TableCell>

                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getMatchTypeIcon(matchType)}
                        <Chip
                          label={
                            matchType === "fuzzy"
                              ? `${matchType} (${Math.round(
                                  similarity * 100
                                )}%)`
                              : matchType
                          }
                          size="small"
                          color={getMatchTypeColor(matchType)}
                          variant="outlined"
                        />
                      </Box>
                    </TableCell>

                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={targetCol}
                        onChange={(e) =>
                          handleMappingChange(excelCol, e.target.value)
                        }
                        sx={{ minWidth: 150 }}
                      >
                        {/* Existing columns */}
                        {availableColumns.map((col) => (
                          <MenuItem key={col} value={col}>
                            {col}
                          </MenuItem>
                        ))}
                        <Divider />
                        {/* New column option */}
                        <MenuItem value={excelCol}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AddIcon fontSize="small" color="info" />
                            Create "{excelCol}"
                          </Box>
                        </MenuItem>
                      </TextField>
                    </TableCell>

                    <TableCell>
                      {tableType ? (
                        <Chip
                          label={tableType}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label={excelType}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      {targetCol === excelCol &&
                      !table_info.existing_columns[targetCol] ? (
                        <Chip label="Create" size="small" color="info" />
                      ) : (
                        <Chip label="Map" size="small" color="success" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary Accordions */}
        <Box>
          {mapping_analysis.exact_matches.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography>
                    Exact Matches ({mapping_analysis.exact_matches.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  These columns have exact name matches and will be mapped
                  automatically.
                </Typography>
                {mapping_analysis.exact_matches.map((match) => (
                  <Chip
                    key={match.excel_column}
                    label={`${match.excel_column} → ${match.table_column}`}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </AccordionDetails>
            </Accordion>
          )}

          {mapping_analysis.fuzzy_matches.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SwapHorizIcon color="warning" fontSize="small" />
                  <Typography>
                    Fuzzy Matches ({mapping_analysis.fuzzy_matches.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  These columns have similar names and are suggested mappings.
                  You can adjust them above.
                </Typography>
                {mapping_analysis.fuzzy_matches.map((match) => (
                  <Chip
                    key={match.excel_column}
                    label={`${match.excel_column} → ${
                      match.table_column
                    } (${Math.round(match.similarity * 100)}%)`}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </AccordionDetails>
            </Accordion>
          )}

          {mapping_analysis.new_columns.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <AddIcon color="info" fontSize="small" />
                  <Typography>
                    New Columns ({mapping_analysis.new_columns.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  These columns don't exist in the target table and will be
                  created automatically.
                </Typography>
                {mapping_analysis.new_columns.map((col) => (
                  <Chip
                    key={col.excel_column}
                    label={`${col.excel_column} (${col.excel_type})`}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={validationErrors.length > 0}
        >
          Confirm Mapping
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnMappingDialog;
